/**
 * Process flat cell array into 2D grid structure
 * @param {Array} cells - Array of {rowIndex, columnIndex, value}
 * @returns {Object} - {rows: 2D array, maxRow: number, maxCol: number}
 */
export const processTableData = (cells) => {
  if (!cells || cells.length === 0 || !cells.map) {
    return { rows: [], maxRow: 0, maxCol: 0, cellMetadata: {} };
  }

  // Find table dimensions
  const maxRow = Math.max(...cells.map((c) => c.rowIndex)) + 1;
  const maxCol = Math.max(...cells.map((c) => c.columnIndex)) + 1;

  // Create 2D array filled with null
  const rows = Array(maxRow)
    .fill(null)
    .map(() => Array(maxCol).fill(null));

  // Store cell metadata (format, type, etc.)
  const cellMetadata = {};

  // Populate cells with values and metadata
  cells.forEach((cell) => {
    if (cell.rowIndex >= 0 && cell.columnIndex >= 0 && cell.rowIndex < maxRow && cell.columnIndex < maxCol) {
      rows[cell.rowIndex][cell.columnIndex] = cell.value;

      // Store metadata if present
      const key = `${cell.rowIndex}-${cell.columnIndex}`;
      cellMetadata[key] = {
        type: cell.type || 'text',
        bold: cell.bold || false,
        italic: cell.italic || false,
        underline: cell.underline || false,
        strike: cell.strike || false,
        backgroundColor: cell.backgroundColor,
        textColor: cell.textColor,
        formula: cell.formula,
      };
    }
  });

  return { rows, maxRow, maxCol, cellMetadata };
};

/**
 * Convert column index to Excel-style letter (A, B, C, ... Z, AA, AB...)
 * @param {number} index - Zero-based column index
 * @returns {string} - Column label
 */
export const getColumnLabel = (index) => {
  let label = '';
  let num = index;

  while (num >= 0) {
    label = String.fromCharCode(65 + (num % 26)) + label;
    num = Math.floor(num / 26) - 1;
  }

  return label;
};

/**
 * Validate table data structure
 * @param {Array} data - Array of cell objects
 * @returns {Array} - Array of error messages (empty if valid)
 */
export const validateTableData = (data) => {
  const errors = [];

  if (!Array.isArray(data)) {
    errors.push('Data must be an array');
    return errors;
  }

  data.forEach((cell, index) => {
    if (typeof cell !== 'object' || cell === null) {
      errors.push(`Cell ${index}: must be an object`);
      return;
    }

    if (typeof cell.rowIndex !== 'number') {
      errors.push(`Cell ${index}: rowIndex must be a number`);
    }

    if (typeof cell.columnIndex !== 'number') {
      errors.push(`Cell ${index}: columnIndex must be a number`);
    }

    if (cell.rowIndex < 0 || cell.columnIndex < 0) {
      errors.push(`Cell ${index}: indices must be non-negative`);
    }

    if (cell.value === undefined) {
      errors.push(`Cell ${index}: value is required`);
    }
  });

  return errors;
};

/**
 * Format cell value based on type
 * @param {any} value - Cell value
 * @param {string} type - Value type (text, number, currency, percentage, date)
 * @returns {string} - Formatted value
 */
export const formatCellValue = (value, type = 'text') => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  switch (type) {
    case 'number':
      return typeof value === 'number' ? value.toLocaleString() : value;

    case 'currency':
      return typeof value === 'number'
        ? `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : value;

    case 'percentage':
      return typeof value === 'number' ? `${value}%` : value;

    case 'date':
      try {
        return new Date(value).toLocaleDateString();
      } catch (e) {
        return value;
      }

    case 'text':
    default:
      return String(value);
  }
};
